// Libs
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';

// API
import postPriceAndStock from '@/common/api/products/postPriceAndStock';
// Constants
import {
  ENDPOINT_PRICE_AND_STOCK,
  MAX_RETRY_DELAY,
  NO_CONTENT,
  PARTIAL_CONTENT,
  PRICE_OBJECT_TYPE,
} from '@/common/constants';
// Contexts
import { useAuth } from '@/common/contexts/AuthContext';
import priceAdapter from '@/common/helpers/priceAdapter';

const withPriceAndStock = (Children) => (props) => {
  const { zergRush } = useAuth();
  const {
    /* eslint-disable react/prop-types */
    apiPost,
    availableQuantity,
    delayQuantity,
    customerPrice,
    discountPrice,
    hasPrice,
    hasStock,
    invoiceUnit,
    loyaltyPoint,
    sku,
    onReceivePrice,
    publicPrice,
    price,
    saleUnit,
    taxRate,
    stockStatus,
    stocks,
    tierPrices,
    weeeAmount,
    splitPackaging,
    salesPackaging,
    /* eslint-enable react/prop-types */
  } = props;
  // eslint-disable-next-line react/prop-types,react/destructuring-assignment
  const active = props?.withPriceAndStockHocActive ?? true;

  const [currentPriceAndStock, setCurrentPriceAndStock] = useState({
    availableQuantity,
    delayQuantity,
    customerPrice,
    discountPrice,
    hasPrice,
    hasStock,
    invoiceUnit,
    loyaltyPoint,
    publicPrice,
    price,
    saleUnit,
    taxRate,
    stockStatus,
    stocks,
    tierPrices,
    weeeAmount,
    splitPackaging,
    salesPackaging,
  });

  const newProps = {
    ...props,
    availableQuantity: currentPriceAndStock.availableQuantity,
    customerPrice: currentPriceAndStock.customerPrice,
    delayQuantity: currentPriceAndStock.delayQuantity,
    discountPrice: currentPriceAndStock.discountPrice,
    hasPrice: currentPriceAndStock.hasPrice,
    hasStock: currentPriceAndStock.hasStock,
    invoiceUnit: currentPriceAndStock.invoiceUnit,
    loyaltyPoint: currentPriceAndStock.loyaltyPoint,
    price: price || currentPriceAndStock.price, // CAUTION: Fix CartItem price param as number TODO: remove withPriceAndStock from CartItem ?
    publicPrice: currentPriceAndStock.publicPrice,
    saleUnit: currentPriceAndStock.saleUnit,
    stockStatus: currentPriceAndStock.stockStatus,
    stocks: currentPriceAndStock.stocks,
    taxRate: currentPriceAndStock.taxRate,
    tierPrices: currentPriceAndStock.tierPrices,
    weeeAmount: currentPriceAndStock.weeeAmount,
    splitPackaging: currentPriceAndStock.splitPackaging || false,
    salesPackaging: currentPriceAndStock.salesPackaging || 1,
  };

  const hasNoPriceAndStock = useMemo(
    () => (!hasPrice || !hasStock) && !zergRush,
    [hasPrice, hasStock, zergRush],
  );

  // const { data: newPriceAndStock } = usePriceAndStock(
  //   mainSku,
  //   hasNoPriceAndStock,
  // );

  // TODO:
  //  - Clean HOC,
  //  - Replace newProps/setCurrentPriceAndStock by useMemo
  //  - ...
  const { mutate: mutatePostPriceAndStock } = useMutation(
    apiPost ?? postPriceAndStock,
  );
  const onPostPriceAndStock = useCallback(
    (retry = 0) => {
      mutatePostPriceAndStock(
        { queryKey: [ENDPOINT_PRICE_AND_STOCK, sku] },
        {
          onSuccess: (res) => {
            const newPriceAndStock = res.data;

            setCurrentPriceAndStock({
              availableQuantity: newPriceAndStock.available_quantity,
              customerPrice: priceAdapter({
                price: newPriceAndStock.price,
                type: PRICE_OBJECT_TYPE.CUSTOMER,
              }),
              delayQuantity: newPriceAndStock.delay_quantity,
              discountPrice: newPriceAndStock.price?.discount_price,
              hasPrice: newPriceAndStock.has_price,
              hasStock: newPriceAndStock.has_stock,
              invoiceUnit: newPriceAndStock.invoice_unit,
              loyaltyPoint: newPriceAndStock.loyalty_point,
              price: newPriceAndStock.price,
              publicPrice: priceAdapter({
                price: newPriceAndStock.price,
                type: PRICE_OBJECT_TYPE.PUBLIC,
              }),
              saleUnit: newPriceAndStock.sale_unit,
              stockStatus: newPriceAndStock.stock_status,
              stocks: newPriceAndStock.stocks,
              taxRate: newPriceAndStock?.tax_rate ?? null,
              tierPrices: newPriceAndStock.price?.tier_prices,
              weeeAmount: priceAdapter({
                price: newPriceAndStock.price,
                type: PRICE_OBJECT_TYPE.WEEE_AMOUNT,
              }),
              splitPackaging: newPriceAndStock.split_packaging,
              salesPackaging: newPriceAndStock.sales_packaging,
            });

            /**
             * Check is prices and onReceivePrice exist and send there to the handleOnReceivePrice callback
             */
            if (
              newPriceAndStock.price?.public_price &&
              newPriceAndStock.price?.customer_price &&
              onReceivePrice
            ) {
              onReceivePrice(newPriceAndStock);
            }

            if (
              (res.status === NO_CONTENT || res.status === PARTIAL_CONTENT) &&
              retry < 3
            ) {
              setTimeout(
                () => onPostPriceAndStock(retry + 1),
                Math.min(2000 * 2 ** retry, MAX_RETRY_DELAY),
              );
            }
          },
          onError: (error) => {
            // eslint-disable-next-line no-console
            console.log(error);
          },
        },
      );
    },
    [mutatePostPriceAndStock, onReceivePrice, sku],
  );

  useEffect(() => {
    if (hasNoPriceAndStock && active) {
      onPostPriceAndStock();
    }
  }, [active, hasNoPriceAndStock, onPostPriceAndStock]);

  // TODO: ne pas effecer, il y a un trie à faire
  // useLayoutEffect(() => {
  //   if (newPriceAndStock) {
  //     setCurrentPriceAndStock({
  //       availableQuantity: newPriceAndStock.available_quantity,
  //       delayQuantity: newPriceAndStock.delay_quantity,
  //       customerPrice: newPriceAndStock.price?.customer_price,
  //       discountPrice: newPriceAndStock.price?.discount_price,
  //       hasPrice: newPriceAndStock.has_price,
  //       hasStock: newPriceAndStock.has_stock,
  //       invoiceUnit: newPriceAndStock.invoice_unit,
  //       loyaltyPoint: newPriceAndStock.loyalty_point,
  //       publicPrice: newPriceAndStock.price?.public_price,
  //       saleUnit: newPriceAndStock.sale_unit,
  //       stockStatus: newPriceAndStock.stock_status,
  //       stocks: newPriceAndStock.stocks,
  //       tierPrices: newPriceAndStock.price?.tier_prices,
  //       weeeAmount: newPriceAndStock.price?.weee_amount,
  //     });
  //   }
  // }, [newPriceAndStock]);
  // eslint-disable-next-line react/jsx-props-no-spreading
  if (!active) return <Children {...props} />;
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Children {...newProps} />;
};
export default withPriceAndStock;
